
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { createRouteAwareChannelName, shouldProcessEvent, isInRoute } from '@/utils/routeManager';

interface AdminContextType {
  isAdminPage: boolean;
  setIsAdminPage: (value: boolean) => void;
  adminData: any;
  refreshAdminData: () => Promise<void>;
  isAdmin: boolean;
  isLoadingAdmin: boolean;
}

const AdminContext = createContext<AdminContextType>({
  isAdminPage: false,
  setIsAdminPage: () => {},
  adminData: null,
  refreshAdminData: async () => {},
  isAdmin: false,
  isLoadingAdmin: true,
});

export const useAdmin = () => useContext(AdminContext);

export const AdminProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const { isAdmin, isLoading: isLoadingAdmin } = useAdminAuth();
  const [isAdminPage, setIsAdminPage] = useState(false);
  const [adminData, setAdminData] = useState(null);
  
  // Controles ESPECÍFICOS para admin com verificação de rota
  const adminListenerSetup = useRef(false);
  const adminChannelRef = useRef<any>(null);
  const adminRouteActive = useRef(false);

  const refreshAdminData = async () => {
    if (!isAdmin || !isAdminPage || !isInRoute('/paineladmin')) {
      console.log('Admin: Refresh ignorado - não está no contexto admin correto');
      return;
    }
    
    try {
      console.log('Admin: Carregando dados do painel administrativo (manual e isolado)');
      // Implementar lógica de carregamento de dados admin aqui se necessário
    } catch (error) {
      console.error('Admin: Erro ao carregar dados:', error);
    }
  };

  // Listener removido - duplicado com useAdminPanel.ts

  // Cleanup global quando o componente é desmontado
  useEffect(() => {
    return () => {
      if (adminChannelRef.current) {
        console.log('Admin: Cleanup final e DEFINITIVO do AdminProvider');
        supabase.removeChannel(adminChannelRef.current);
        adminChannelRef.current = null;
        adminListenerSetup.current = false;
        adminRouteActive.current = false;
      }
    };
  }, []);

  // Verificar se a página mudou para fora do admin
  useEffect(() => {
    const handleRouteChange = () => {
      const currentPath = window.location.pathname;
      if (!currentPath.startsWith('/paineladmin') && adminRouteActive.current) {
        console.log('Admin: Rota mudou para fora do admin - limpando listeners');
        if (adminChannelRef.current) {
          supabase.removeChannel(adminChannelRef.current);
          adminChannelRef.current = null;
          adminListenerSetup.current = false;
          adminRouteActive.current = false;
        }
      }
    };

    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  return (
    <AdminContext.Provider value={{
      isAdminPage,
      setIsAdminPage,
      adminData,
      refreshAdminData,
      isAdmin,
      isLoadingAdmin
    }}>
      {children}
    </AdminContext.Provider>
  );
};
